import { useRouter } from '@uirouter/react';

import type { Environment } from '@/core/environments/types';
import { Button } from '@/core/components/Button';
import { confirmAsync } from '@/core/services/modal.service/confirm';
import { promptAsync } from '@/core/services/modal.service/prompt';
import * as notifications from '@/core/services/notifications';
import { activateDevice } from '@/core/hostmanagement/open-amt/open-amt.service';
import { deleteEndpoint } from '@/core/environments/environment.service';

interface Props {
  selectedItems: Environment[];
  isFDOEnabled: boolean;
  isOpenAMTEnabled: boolean;
  setLoadingMessage(message: string): void;
}

export function EdgeDevicesDatatableActions({
  selectedItems,
  isOpenAMTEnabled,
  isFDOEnabled,
  setLoadingMessage,
}: Props) {
  const router = useRouter();

  return (
    <div className="actionBar">
      <Button
        disabled={selectedItems.length < 1}
        color="danger"
        onClick={() => onDeleteEdgeDeviceClick()}
      >
        <i className="fa fa-trash-alt space-right" aria-hidden="true" />
        Удалить
      </Button>

      <Button onClick={() => onAddNewDeviceClick()}>
        <i className="fa fa-plus-circle space-right" aria-hidden="true" />
        Добавить устройство
      </Button>

      {isOpenAMTEnabled && (
        <Button
          disabled={selectedItems.length !== 1}
          onClick={() => onAssociateOpenAMTClick(selectedItems)}
        >
          <i className="fa fa-link space-right" aria-hidden="true" />
          Связать с OpenAMT
        </Button>
      )}
    </div>
  );

  async function onDeleteEdgeDeviceClick() {
    const confirmed = await confirmAsync({
      title: 'Вы уверены?',
      message:
        'Будут удалены все конфигурации, связанные с окружениями. Продолжить?',
      buttons: {
        confirm: {
          label: 'Удалить',
          className: 'btn-danger',
        },
      },
    });

    if (!confirmed) {
      return;
    }

    await Promise.all(
      selectedItems.map(async (environment) => {
        try {
          await deleteEndpoint(environment.Id);

          notifications.success('Окружение удалено', environment.Name);
        } catch (err) {
          notifications.error(
            'Ошибка',
            err as Error,
            'Не удалось удалить окружение'
          );
        }
      })
    );

    await router.stateService.reload();
  }

  async function onAddNewDeviceClick() {
    if (!isFDOEnabled) {
      router.stateService.go('shbdeployer.endpoints.newEdgeDevice');
      return;
    }

    const result = await promptAsync({
      title: 'Каким способом добавить Edge-устройство?',
      inputType: 'radio',
      inputOptions: [
        {
          text: 'Подготовить физический сервер через Intel FDO',
          value: 'FDO',
        },
        {
          text: 'Установить агент вручную',
          value: 'MANUAL',
        },
      ],
      buttons: {
        confirm: {
          label: 'Подтвердить',
          className: 'btn-primary',
        },
      },
    });

    switch (result) {
      case 'FDO':
        router.stateService.go('shbdeployer.endpoints.importDevice');
        break;
      case 'MANUAL':
        router.stateService.go('shbdeployer.endpoints.newEdgeDevice');
        break;
      default:
        break;
    }
  }

  async function onAssociateOpenAMTClick(selectedItems: Environment[]) {
    const selectedEnvironment = selectedItems[0];

    const confirmed = await confirmAsync({
      title: '',
      message: `Associate ${selectedEnvironment.Name} with OpenAMT`,
      buttons: {
        cancel: {
          label: 'Отмена',
          className: 'btn-default',
        },
        confirm: {
          label: 'Подтвердить',
          className: 'btn-primary',
        },
      },
    });

    if (!confirmed) {
      return;
    }

    try {
      setLoadingMessage(
        'Включение Active Management Technology на выбранном устройстве…'
      );
      await activateDevice(selectedEnvironment.Id);
      notifications.success(
        'Устройство связано с OpenAMT',
        selectedEnvironment.Name
      );
    } catch (err) {
      notifications.error(
        'Ошибка',
        err as Error,
        'Не удалось связать устройство с OpenAMT'
      );
    } finally {
      setLoadingMessage('');
    }
  }
}
